import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Send, CheckCircle, Clock, Download } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

export default function MandateStatus({ caseId, clientPhone }) {
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/cases/${caseId}/mandate`);
      setMandate(r.data);
    } catch (err) {
      console.error('Mandate load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [caseId]);

  const sendMandate = async (language = 'fr') => {
    if (!window.confirm(`Envoyer le lien de signature par WhatsApp au client ?`)) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/cases/${caseId}/mandate/send`, { language });
      await load();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const r = await axios.get(`${API}/api/cases/${caseId}/mandate/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mandat_${caseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur lors du téléchargement: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) return null;

  const isSigned = mandate?.status === 'signed';
  const isPending = mandate?.status === 'pending';
  const isNone = !mandate || mandate.status === 'none';

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <FileText size={16} color="#0D1B3E" />
          <span style={styles.title}>Mandat client</span>
        </div>
        <div style={{
          ...styles.badge,
          background: isSigned ? '#DCFCE7' : isPending ? '#FEF3C7' : '#F3F4F6',
          color: isSigned ? '#0D9F57' : isPending ? '#D97706' : '#9CA3AF'
        }}>
          {isSigned ? '✅ Signé' : isPending ? '⏳ En attente' : '⚪ Non envoyé'}
        </div>
      </div>

      {isSigned && (
        <div style={styles.signedInfo}>
          <CheckCircle size={14} color="#0D9F57" />
          <span style={styles.signedText}>
            Signé le {new Date(mandate.signed_at).toLocaleString('fr-FR', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })} par {mandate.signer_name}
          </span>
        </div>
      )}

      {isPending && mandate.expires_at && (
        <div style={styles.pendingInfo}>
          <Clock size={14} color="#D97706" />
          <span style={styles.pendingText}>
            Expire le {new Date(mandate.expires_at).toLocaleDateString('fr-FR')}
          </span>
        </div>
      )}

      <div style={styles.actions}>
        {!isSigned && (
          <div style={styles.sendGroup}>
            <button
              onClick={() => sendMandate('fr')}
              disabled={sending}
              style={styles.sendBtn}
            >
              <Send size={13} />
              {sending ? 'Envoi...' : isPending ? '🔄 Renvoyer (FR)' : '📤 Envoyer (FR)'}
            </button>
            <button
              onClick={() => sendMandate('en')}
              disabled={sending}
              style={{ ...styles.sendBtn, background: '#3B82F6' }}
            >
              <Send size={13} />
              {sending ? '...' : 'EN'}
            </button>
          </div>
        )}

        {isSigned && mandate.pdf_path && (
          <button onClick={downloadPDF} style={styles.downloadBtn}>
            <Download size={13} /> Télécharger le PDF signé
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    background: '#fff', borderRadius: 14, padding: 18,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 12,
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8 },
  title: { fontSize: 13, fontWeight: 800, color: '#0D1B3E', textTransform: 'uppercase', letterSpacing: 0.5 },
  badge: { fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 100 },
  signedInfo: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 },
  signedText: { fontSize: 13, color: '#0D9F57' },
  pendingInfo: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 },
  pendingText: { fontSize: 13, color: '#D97706' },
  actions: { display: 'flex', flexDirection: 'column', gap: 8 },
  sendGroup: { display: 'flex', gap: 8 },
  sendBtn: {
    display: 'flex', alignItems: 'center', gap: 6, flex: 1,
    justifyContent: 'center', background: '#0D1B3E', color: '#fff',
    border: 'none', borderRadius: 8, padding: '10px 14px',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  downloadBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#0D9F57', color: '#fff', border: 'none',
    borderRadius: 8, padding: '10px 14px', fontSize: 13,
    fontWeight: 700, cursor: 'pointer',
  },
};
